
import React, { useState, useEffect } from 'react';
import { Customer } from '../types';
import CustomerTable from './CustomerTable';
import { api } from '../services/api';

interface CustomersProps {
  onCustomerSelect?: (id: string) => void;
}

const Customers: React.FC<CustomersProps> = ({ onCustomerSelect }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await api.customers.getAll();
        if (data && Array.isArray(data)) {
          setCustomers(data);
        }
      } catch (err) {
        console.error("Failed to fetch customers", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSelect = (customer: Customer) => {
    if (onCustomerSelect) {
      onCustomerSelect(customer.id);
    }
  };

  const totalLeads = customers.length;
  const paidCustomers = customers.filter(c => String(c.status).toLowerCase() === 'active').length;
  const conversionRate = totalLeads > 0 ? ((paidCustomers / totalLeads) * 100).toFixed(1) : '0.0';
  const avgLtv = totalLeads > 0 ? Math.round(customers.reduce((sum, c) => sum + Number(c.mrr || c.ltv || 0), 0) / totalLeads) : 0;

  return (
    <div className="space-y-6 animate-in fade-in duration-500 relative">
      <div className="flex justify-between items-center">
        <div>
           <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Customer CRM</h1>
           <p className="text-slate-500 dark:text-slate-400 text-sm">Manage user profiles, identities, and lifecycle value.</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <input type="text" placeholder="Search by name or email..." className="pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none w-64" />
            <div className="absolute left-3 top-2.5 text-slate-400">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
            </div>
          </div>
          <button className="px-6 py-2.5 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 dark:shadow-none active:scale-95">Add Customer</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
         <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm transition-all hover:shadow-md">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Leads</p>
            <p className="text-2xl font-black text-slate-900 dark:text-white">{totalLeads}</p>
         </div>
         <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm transition-all hover:shadow-md">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Paid Customers</p>
            <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{paidCustomers}</p>
         </div>
         <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm transition-all hover:shadow-md">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Conversion Rate</p>
            <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400">{conversionRate}%</p>
         </div>
         <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm transition-all hover:shadow-md">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Average LTV</p>
            <p className="text-2xl font-black text-slate-900 dark:text-white">${avgLtv.toLocaleString()}</p>
         </div>
      </div>

      {loading && customers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-sm">
          <svg className="animate-spin h-8 w-8 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-slate-400 text-xs font-black uppercase tracking-widest">Hydrating Customer Vault...</p>
        </div>
      ) : (
        <CustomerTable customers={customers} onSelect={handleSelect} />
      )}
    </div>
  );
};

export default Customers;
